import { Box, Card, Typography } from '@mui/material';
import { useGame } from 'contexts/Game/Game';
import { usePage } from 'contexts/Page/Page';
import { getUrl, parseHtml } from 'helpers';
import _ from 'lodash';
import { useMemo } from 'react';
import * as setup from 'setup';
import WizardButton from '../WizardButton/WizardButton';

export default function Wizard({ children, lesson, variables, onAction }: WizardProps) {

    const page = usePage();
    const game = useGame();

    const lessonContent = useMemo(() => {
        const key = lesson || page.lesson;
        const lessonContent: WizardContent | null = ((key && setup.lessons[key]) || null);
        if (lessonContent?.clone) {
            return {
                ...(setup.lessons[lessonContent.clone] || {}),
                ...lessonContent,
            };
        }
        return lessonContent;
    }, [
        lesson,
        page.lesson,
    ]);

    const gameVariables = useMemo(() => {
        return {
            foodType: game.foodType,
            predictionConversions: game.predictions.conversions.toLocaleString(),
            predictionInitial: game.predictions.initial.toLocaleString(),
            predictionInitialMonthly: game.predictions.initialMonthly.toLocaleString(),
            predictionMax: setup.game.prediction.max.toLocaleString(),
            predictionMin: setup.game.prediction.min.toLocaleString(),
            predictionSteps: game.predictions.steps.toLocaleString(),
            truckName: game.truckName,
        };
    }, [
        game.foodType,
        game.predictions.conversions,
        game.predictions.initial,
        game.predictions.initialMonthly,
        game.predictions.steps,
        game.truckName,
    ]);

    const vars = useMemo(() => {
        return {
            ...gameVariables,
            ...variables,
        };
    }, [
        gameVariables,
        variables,
    ]);

    const buttons = useMemo(() => {
        return [
            lessonContent?.button1,
            lessonContent?.button2,
            lessonContent?.button3,
            lessonContent?.button4,
        ]
            .filter(button => button)
            .map(button => {
                let label = _.sample(button?.labels) || button?.label || '';
                if (typeof label === 'function') {
                    label = label(vars);
                }
                return {
                    ...button,
                    label: parseHtml(label, vars),
                } as WizardButtonProps;
            });
    }, [
        lessonContent,
        vars,
    ]);

    const text = useMemo(() => {
        let text = _.sample(lessonContent?.texts) || lessonContent?.text || '';
        if (typeof text === 'function') {
            text = text(vars);
        }
        return parseHtml(text, vars);
    }, [
        lessonContent,
        vars,
    ]);

    return (
        <Card aria-label="Lesson content" data-testid="wizard-card" sx={{ maxWidth: 376 }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5,
                    padding: 2.5
                }}>
                {(lessonContent?.image || lessonContent?.title) && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column-reverse',
                            gap: 2.5
                        }}>
                        <Typography component="h1" dangerouslySetInnerHTML={{ __html: lessonContent?.title || '' }} variant="montserrat529" sx={{
                            color: '#1375BC'
                        }} />
                        {lessonContent?.image && (
                            <Box
                                sx={{
                                    marginX: 'auto',
                                    maxWidth: 360,
                                    width: '100%'
                                }}>
                                <Box
                                    sx={{
                                        paddingBottom: `${(170 / 360) * 100}%`,
                                        position: 'relative'
                                    }}>
                                    <Box
                                        sx={{
                                            alignItems: 'center',
                                            display: 'flex',
                                            height: '100%',
                                            justifyContent: 'center',
                                            left: 0,
                                            position: 'absolute',
                                            top: 0,
                                            width: '100%'
                                        }}>
                                        <picture>
                                            <img alt={lessonContent?.imageAlt ?? ''} src={getUrl(lessonContent.image)} style={{ maxHeight: 170, maxWidth: '100%' }} />
                                        </picture>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            {text && (
                <Box
                    dangerouslySetInnerHTML={{ __html: text }}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                    }} />
            )}
            {children}
                {buttons.map((button, index) => (
                    <WizardButton key={index} {...button} onAction={onAction} />
                ))}
            </Box>
        </Card>
    );

}
